import type { TFunction } from 'i18next';

import {
  UserType,
  type GetUserV2Response,
  type PersonV1CurrentResponse,
} from 'client/types.gen';

import { getPersonName, isPersonExecutive } from 'helpers/person';

export type UserViewMetaSection =
  | 'people'
  | 'trending'
  | 'events'
  | 'pipelines';

const USER_VIEW_META_DEFAULTS: Record<UserViewMetaSection, string> = {
  people: '$t(contact.company): Network',
  trending: '$t(contact.company): Featured',
  events: '$t(contact.company): Events',
  pipelines: '$t(contact.company): Requests',
};

/** Document title for `/users/:id/*` executive-facing pages (People, Trending, etc.). */
export function userViewMetaTitle(
  t: TFunction,
  section: UserViewMetaSection,
): string {
  return t(`page.user.view.${section}.metaSelf`, {
    defaultValue: USER_VIEW_META_DEFAULTS[section],
  });
}

/** Placeholder for fields not returned by `GET /people/current` (not shown on exec self routes). */
const PLACEHOLDER_CREATED = '1970-01-01T00:00:00.000Z';

/**
 * Build a {@link GetUserV2Response}-shaped object from the signed-in person so we can skip
 * `GET /api/v2/users/{uuid}` (often admin-only) when an executive views their own `/users/:id/*` URLs.
 */
export function viewedUserForExecutiveSelf(
  person: PersonV1CurrentResponse,
): GetUserV2Response {
  const uuid = person.uuid;
  const fullName =
    getPersonName(person) || person.person_name || person.name || null;

  return {
    active: true,
    created: PLACEHOLDER_CREATED,
    email: person.email,
    first_name: person.first_name ?? null,
    full_name: fullName,
    id: uuid,
    uuid,
    last_login: person.last_login ?? null,
    last_name: person.last_name ?? null,
    pending: false,
    permit_preregistration_notifications: false,
    teams_count: 0,
    user_type: isPersonExecutive(person) ? 'executive' : 'enterprise',
  };
}

/** Whether this user gets the `/users/:id/*` executive shell (People, Trending, etc.). */
export function viewedUserHasExecutiveUserView(
  user: GetUserV2Response | undefined | null,
): boolean {
  if (!user) {
    return false;
  }

  const withExecutiveFlag = user as GetUserV2Response & {
    is_executive?: boolean;
  };
  if (withExecutiveFlag.is_executive === true) {
    return true;
  }

  return user.user_type === UserType.EXECUTIVE;
}

export function isUsersRouteExecutiveSelf(
  person: PersonV1CurrentResponse | undefined | null,
  userIdParam: string,
): boolean {
  if (!person || !isPersonExecutive(person)) {
    return false;
  }
  return person.uuid === userIdParam;
}
